import React from 'react';

import LazyLoadedImage from 'common/LazyLoadedImage';
import { IntegrationButtonDefaultProps } from 'common/subdomain/admin/AdminIntegrationSettings/AdminIntegrationCard/constant';
import {
  type IntegrationLinkCard,
  Tag,
} from 'common/subdomain/admin/AdminIntegrationSettings/constants';
import Badge from 'common/uiV3/Badge';
import Button from 'common/uiV3/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import Flex from 'common/uiV3/Flex';
import HStack from 'common/uiV3/HStack';
import ZapierPlatformLogo from 'img/zapier-platform-logo-small.svg';

type IntegrationLinkProps = {
  label: string;
  children: React.ReactNode;
  destination: string;
  external?: boolean;
  onClick?: () => void;
};

const IntegrationLink = ({
  label,
  children,
  destination,
  external,
  onClick,
}: IntegrationLinkProps) => {
  const linkHint = `Configure ${label} settings.`;

  // onClick handles upsell, so it has to  be before the `external` check
  if (onClick) {
    return (
      <Button {...IntegrationButtonDefaultProps} aria-label={linkHint} onClick={onClick}>
        {children}
      </Button>
    );
  }

  if (external) {
    const externalLinkHint = `Configure ${label} in a new tab.`;
    return (
      <ButtonLink
        href={destination}
        target="_blank"
        rel="noopener"
        {...IntegrationButtonDefaultProps}
        aria-label={externalLinkHint}>
        {children}
      </ButtonLink>
    );
  }

  return (
    <ButtonLink aria-label={linkHint} {...IntegrationButtonDefaultProps} to={destination}>
      {children}
    </ButtonLink>
  );
};

type RightHandInfoProps = { tag?: Tag; zapier?: boolean };
const RightHandInfo = ({ tag, zapier }: RightHandInfoProps) => {
  if (tag) {
    return (
      <Flex className="justify-end">
        <Badge variant={tag === Tag.beta ? 'beta' : 'canny'}>{tag}</Badge>
      </Flex>
    );
  }

  if (zapier) {
    return (
      <Flex className="justify-end">
        <span className="flex flex-col items-center text-sm text-secondary-foreground">
          via <LazyLoadedImage className="h-3 align-middle" src={ZapierPlatformLogo} />
        </span>
      </Flex>
    );
  }

  return null;
};

type Props = {
  integration: IntegrationLinkCard;
};

const AdminIntegrationLinkButton = ({ integration }: Props) => {
  const { tag, icon: Icon } = integration;

  return (
    <IntegrationLink
      label={integration.label}
      destination={integration.url}
      external={integration.external}
      onClick={integration.onClick}>
      <HStack className="flex-1 items-center gap-3 min-w-0">
        {typeof Icon === 'string' ? (
          <LazyLoadedImage src={Icon} className="w-8 h-auto" alt="" />
        ) : (
          Icon
        )}
        <p className="truncate">{integration.label}</p>
      </HStack>
      <RightHandInfo tag={tag} zapier={integration.zapier} />
    </IntegrationLink>
  );
};

export default AdminIntegrationLinkButton;
